import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, getDocs, doc, updateDoc, query, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Correct collection path for bugReports
const BUGS_COLLECTION_REF = collection(db, 'gameTesting/bugReports/bugReports');

// Async thunks
export const fetchUserBugs = createAsyncThunk(
  'bugResolution/fetchUserBugs',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const itemsPerPage = 10;
      
      // Create base query with just ordering and pagination to avoid index requirements
      let bugsQuery = query(
        BUGS_COLLECTION_REF,
        orderBy('submittedAt', 'desc'),
        limit(itemsPerPage * 3) // Fetch more to account for client-side filtering
      );
      
      const snapshot = await getDocs(bugsQuery);
      
      // Convert the docs to data and apply client-side filtering
      let bugs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply filters client-side to avoid index requirements
      if (filters.status && filters.status !== 'all') {
        bugs = bugs.filter(bug => bug.status === filters.status);
      }
      
      if (filters.sensitivity && filters.sensitivity !== 'all') {
        bugs = bugs.filter(bug => bug.sensitivity === filters.sensitivity);
      }
      
      if (filters.gameName) {
        bugs = bugs.filter(bug => bug.gameName === filters.gameName);
      }
      
      // Limit to requested page size after filtering
      bugs = bugs.slice(0, itemsPerPage);
      
      // Get the last visible document for pagination
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      
      // Check if there are more bugs to fetch
      let hasMore = false;
      if (lastDoc) {
        const nextQuery = query(
          BUGS_COLLECTION_REF,
          orderBy('submittedAt', 'desc'),
          startAfter(lastDoc),
          limit(1)
        );
        const nextSnapshot = await getDocs(nextQuery);
        hasMore = !nextSnapshot.empty;
      }
      
      return {
        bugs,
        lastVisible: lastDoc,
        hasMore
      };
    } catch (error) {
      console.error('Error fetching bugs:', error);
      return rejectWithValue(error.message || 'Failed to fetch bug reports');
    }
  }
);

export const loadMoreBugs = createAsyncThunk(
  'bugResolution/loadMoreBugs',
  async (filters = {}, { getState, rejectWithValue }) => {
    try {
      const itemsPerPage = 10;
      const { lastVisible } = getState().bugResolution;
      
      if (!lastVisible) {
        return {
          bugs: [],
          lastVisible: null,
          hasMore: false
        };
      }
      
      // Create base query with just ordering and pagination to avoid index requirements
      let bugsQuery = query(
        BUGS_COLLECTION_REF,
        orderBy('submittedAt', 'desc'),
        startAfter(lastVisible),
        limit(itemsPerPage * 3) // Fetch more to account for client-side filtering
      );
      
      const snapshot = await getDocs(bugsQuery);
      
      // Convert the docs to data and apply client-side filtering
      let bugs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply filters client-side to avoid index requirements
      if (filters.status && filters.status !== 'all') {
        bugs = bugs.filter(bug => bug.status === filters.status);
      }
      
      if (filters.sensitivity && filters.sensitivity !== 'all') {
        bugs = bugs.filter(bug => bug.sensitivity === filters.sensitivity);
      }
      
      if (filters.gameName) {
        bugs = bugs.filter(bug => bug.gameName === filters.gameName);
      }
      
      // Limit to requested page size after filtering
      bugs = bugs.slice(0, itemsPerPage);
      
      // Get the last visible document for pagination
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      
      // Check if there are more bugs to fetch
      let hasMore = false;
      if (lastDoc) {
        const nextQuery = query(
          BUGS_COLLECTION_REF,
          orderBy('submittedAt', 'desc'),
          startAfter(lastDoc),
          limit(1)
        );
        const nextSnapshot = await getDocs(nextQuery);
        hasMore = !nextSnapshot.empty;
      }
      
      return {
        bugs,
        lastVisible: lastDoc,
        hasMore
      };
    } catch (error) {
      console.error('Error fetching more bugs:', error);
      return rejectWithValue(error.message || 'Failed to fetch more bug reports');
    }
  }
);

export const resetBugs = createAsyncThunk(
  'bugResolution/resetBugs',
  async () => {
    return true;
  }
);

export const updateBugStatus = createAsyncThunk(
  'bugResolution/updateBugStatus',
  async ({ bugId, status, updatedBy }, { rejectWithValue }) => {
    try {
      const bugRef = doc(BUGS_COLLECTION_REF, bugId);
      
      await updateDoc(bugRef, {
        status,
        lastUpdated: new Date(),
        updatedBy
      });
      
      return {
        bugId,
        status,
        lastUpdated: new Date(),
        updatedBy
      };
    } catch (error) {
      console.error('Error updating bug status:', error);
      return rejectWithValue(error.message || 'Failed to update bug status');
    }
  }
);

export const assignBug = createAsyncThunk(
  'bugResolution/assignBug',
  async ({ bugId, assignedTo, updatedBy }, { rejectWithValue }) => {
    try {
      const bugRef = doc(BUGS_COLLECTION_REF, bugId);
      
      const updateData = {
        assignedTo,
        lastUpdated: new Date(),
        updatedBy
      };
      
      // Also update status to 'assigned' if it's currently 'pending'
      const currentStatus = 'assigned'; // When assigning, set status to 'assigned'
      updateData.status = currentStatus;
      
      await updateDoc(bugRef, updateData);
      
      return {
        bugId,
        assignedTo,
        status: currentStatus,
        lastUpdated: new Date(),
        updatedBy
      };
    } catch (error) {
      console.error('Error assigning bug:', error);
      return rejectWithValue(error.message || 'Failed to assign bug');
    }
  }
);

const initialState = {
  bugs: [],
  loading: false,
  error: null,
  hasMore: false,
  lastVisible: null,
  
  // For single bug view
  selectedBug: null,
  selectedBugLoading: false,
  selectedBugError: null
};

const bugResolutionSlice = createSlice({
  name: 'bugResolution',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.selectedBugError = null;
    },
    clearSelectedBug: (state) => {
      state.selectedBug = null;
      state.selectedBugError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchUserBugs
      .addCase(fetchUserBugs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserBugs.fulfilled, (state, action) => {
        state.bugs = action.payload.bugs;
        state.lastVisible = action.payload.lastVisible;
        state.hasMore = action.payload.hasMore;
        state.loading = false;
      })
      .addCase(fetchUserBugs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch bug reports';
      })
      
      // loadMoreBugs
      .addCase(loadMoreBugs.pending, (state) => {
        // We don't set loading=true here as we'll use a separate loadingMore state in the component
      })
      .addCase(loadMoreBugs.fulfilled, (state, action) => {
        // Append new bugs
        state.bugs = [...state.bugs, ...action.payload.bugs];
        state.lastVisible = action.payload.lastVisible;
        state.hasMore = action.payload.hasMore;
      })
      .addCase(loadMoreBugs.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch more bug reports';
      })
      
      // resetBugs
      .addCase(resetBugs.fulfilled, (state) => {
        state.bugs = [];
        state.lastVisible = null;
        state.hasMore = false;
        state.error = null;
      })
      
      // updateBugStatus
      .addCase(updateBugStatus.fulfilled, (state, action) => {
        const { bugId, status, lastUpdated, updatedBy } = action.payload;
        
        // Update the bug in the list
        state.bugs = state.bugs.map(bug => 
          bug.id === bugId 
            ? { ...bug, status, lastUpdated, updatedBy } 
            : bug
        );
        
        // Update selected bug if it's the one we modified
        if (state.selectedBug && state.selectedBug.id === bugId) {
          state.selectedBug = {
            ...state.selectedBug,
            status,
            lastUpdated,
            updatedBy
          };
        }
      })
      
      // assignBug
      .addCase(assignBug.fulfilled, (state, action) => {
        const { bugId, assignedTo, status, lastUpdated, updatedBy } = action.payload;
        
        // Update the bug in the list
        state.bugs = state.bugs.map(bug => 
          bug.id === bugId 
            ? { ...bug, assignedTo, status, lastUpdated, updatedBy } 
            : bug
        );
        
        // Update selected bug if it's the one we modified
        if (state.selectedBug && state.selectedBug.id === bugId) {
          state.selectedBug = {
            ...state.selectedBug,
            assignedTo,
            status,
            lastUpdated,
            updatedBy
          };
        }
      });
  }
});

export const { clearErrors, clearSelectedBug } = bugResolutionSlice.actions;

export default bugResolutionSlice.reducer; 